<template>
  <div>
    <div class="row border-bottom white-bg dashboard-header">
      <div class="col-md-6 afstereo-header">
        <img class="afstereo-logo" src="../../assets/images/logos/AFSTEREO.PNG" alt="AFSTEREO Logo" />
        <img class="aircheck-logo" src="../../assets/images/logos/Aircheck.png" alt="Aircheck Logo" />
      </div>
      <div class="col-md-6" v-if="pluginEnabled">
        <div class="form-group pull-right">
          <label>Station</label>
          <el-select v-model="currentStation" filterable placeholder="Select">
            <el-option
              v-for="station in stations"
              :key="station.id"
              :label="station.title"
              :value="station.id"
            ></el-option>
          </el-select>
        </div>
      </div>
    </div>
    <div class="wrapper wrapper-content animated fadeInRight" v-if="pluginEnabled">
      <div class="row">
        <div class="col-lg-3">
          <analytics-unit-block
            :period="selectedDate | moment('h:00 a')"
            :unit="'Hour'"
            :unitClass="'label-success'"
            :description="'Estimated revenue'"
            :selected="selectedPeriod === 'hour'"
            :received="chartsData['hour-stats'].total"
            :growth="chartsData['hour-stats'].growth"
            :stats="chartsData['hour-stats'].points"
            @previous="$emit('previous', { unit: 'hour' })"
            @next="$emit('next', { unit: 'hour' })"
            @click.native="$emit('changePeriod', { unit: 'hour' })"
          >
            <content-loader-placeholder
              v-if="!Boolean(chartsData['hour-stats'].points.length)"
              :type="'AnalyticsUnitBlock'"
              slot="content-loader"
            ></content-loader-placeholder>
          </analytics-unit-block>
        </div>
        <div class="col-lg-3">
          <analytics-unit-block
            :period="selectedDate | moment('dddd Do')"
            :unit="'Day'"
            :unitClass="'label-info'"
            :description="'Estimated revenue'"
            :selected="selectedPeriod === 'day'"
            :received="chartsData['day-stats'].total"
            :growth="chartsData['day-stats'].growth"
            :stats="chartsData['day-stats'].points"
            @previous="$emit('previous', { unit: 'day' })"
            @next="$emit('next', { unit: 'day' })"
            @click.native="$emit('changePeriod', { unit: 'day' })"
          >
            <content-loader-placeholder
              v-if="!Boolean(chartsData['day-stats'].points.length)"
              :type="'AnalyticsUnitBlock'"
              slot="content-loader"
            ></content-loader-placeholder>
          </analytics-unit-block>
        </div>
        <div class="col-lg-3">
          <analytics-unit-block
            :period="selectedDate | moment('MMMM')"
            :unit="'Month'"
            :unitClass="'label-primary'"
            :description="'Estimated revenue'"
            :selected="selectedPeriod === 'month'"
            :received="chartsData['month-stats'].total"
            :growth="chartsData['month-stats'].growth"
            :stats="chartsData['month-stats'].points"
            @previous="$emit('previous', { unit: 'month' })"
            @next="$emit('next', { unit: 'month' })"
            @click.native="$emit('changePeriod', { unit: 'month' })"
          >
            <content-loader-placeholder
              v-if="!Boolean(chartsData['month-stats'].points.length)"
              :type="'AnalyticsUnitBlock'"
              slot="content-loader"
            ></content-loader-placeholder>
          </analytics-unit-block>
        </div>
        <div class="col-lg-3">
          <analytics-unit-block
            :period="selectedDate | moment('YYYY')"
            :unit="'Year'"
            :unitClass="'label-danger'"
            :description="'Estimated revenue'"
            :selected="selectedPeriod === 'year'"
            :received="chartsData['year-stats'].total"
            :growth="chartsData['year-stats'].growth"
            :stats="chartsData['year-stats'].points"
            @previous="$emit('previous', { unit: 'year' })"
            @next="$emit('next', { unit: 'year' })"
            @click.native="$emit('changePeriod', { unit: 'year' })"
          >
            <content-loader-placeholder
              v-if="!Boolean(chartsData['year-stats'].points.length)"
              :type="'AnalyticsUnitBlock'"
              slot="content-loader"
            ></content-loader-placeholder>
          </analytics-unit-block>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-8">
          <i-box contentHeight="480">
            <span slot="title">
              <h5>Engagement and revenue over time</h5>
            </span>
            <span slot="content">
              <analytics-time-series-area-chart
                v-if="Boolean(processEngagementsVsRevenue.length)"
                :data="processEngagementsVsRevenue"
                :unit="units[units.indexOf(selectedPeriod) - 1]"
                :showLegend="false"
                :tension="0.1"
              ></analytics-time-series-area-chart>
              <content-loader-placeholder
                v-if="!Boolean(processEngagementsVsRevenue.length)"
                :type="'EngagementGraphLoader'"
                slot="content-loader"
              ></content-loader-placeholder>
            </span>
          </i-box>
        </div>
        <div class="col-lg-4">
          <i-box contentHeight="480">
            <div slot="title">
              <h5>
                Sold out
                <span v-if="isSoldOutAveraging">average</span> %
              </h5>
            </div>
            <div slot="content">
              <horizontal-bar-chart :showGridLines="true" :data="processSoldOut"></horizontal-bar-chart>
            </div>
          </i-box>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6">
          <i-box :noPadding="true" contentHeight="500">
            <span slot="title">
              <h5>Advertisers</h5>
            </span>
            <span slot="content">
              <div class="container-fluid" v-if="Boolean(processAdvertisers.length)">
                <div class="col-lg-6">
                  <ul class="list-group">
                    <li
                      v-for="(advertiser, index) in chartsData['advertisers'].top"
                      :key="advertiser.advertiser"
                      class="list-group-item small track"
                    >
                      <span class="artist-position pull-left text-bold">{{ index + 1 }}.</span>
                      <div>
                        <growth :value="advertiser.change" :isPercentage="false" class="pull-right"></growth>
                        <span class="track-artist">{{ advertiser.advertiser }}</span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div class="col-lg-6">
                  <analytics-chart type="bar" :scalesEnabled="false" :data="processAdvertisers"></analytics-chart>
                </div>
              </div>
              <content-loader-placeholder
                v-if="!Boolean(processAdvertisers.length)"
                :type="'AdvertisersChartLoader'"
                slot="content-loader"
              ></content-loader-placeholder>
            </span>
          </i-box>
        </div>
        <div class="col-lg-6">
          <i-box :noPadding="true" contentHeight="500">
            <span slot="title">
              <h5>Advertising Categories</h5>
            </span>
            <span slot="content">
              <div class="container-fluid" v-if="Boolean(processCategories.length)">
                <div class="col-lg-6">
                  <ul class="list-group">
                    <li
                      v-for="(category, index) in chartsData['categories'].top"
                      :key="category.category"
                      class="list-group-item small track"
                    >
                      <span class="artist-position pull-left">{{ index + 1 }}.</span>
                      <div>
                        <growth :value="category.change" :isPercentage="false" class="pull-right"></growth>
                        <span class="track-title">{{ category.category }}</span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div class="col-lg-6">
                  <analytics-chart type="bar" :scalesEnabled="false" :data="processCategories"></analytics-chart>
                </div>
              </div>
              <content-loader-placeholder
                v-if="!Boolean(processCategories.length)"
                :type="'AdvertisersChartLoader'"
                slot="content-loader"
              ></content-loader-placeholder>
            </span>
          </i-box>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-4">
          <i-box :noPadding="true">
            <span slot="title">
              <h5>Top 40 tracks</h5>
            </span>
            <span slot="content">
              <ul class="list-group" v-if="Boolean(chartsData['tracks'].top.length)">
                <li
                  v-for="(track, index) in chartsData['tracks'].top"
                  :key="track.track"
                  class="list-group-item small track"
                >
                  <span class="track-position pull-left">{{ index + 1 }}</span>
                  <div>
                    <div class="pull-right text-right">
                      <growth :value="track.change" :isPercentage="false"></growth>
                      <div class="text-muted">{{ track.count.toLocaleString() }}</div>
                    </div>
                    <span class="track-artist text-muted">{{ track.artist }}</span>
                    <br />
                    <span class="track-title">{{ track.title }}</span>
                    <div class="clearfix"></div>
                  </div>
                </li>
              </ul>
              <content-loader-placeholder v-else slot="unit-content" :type="'ListDividerLoader'"></content-loader-placeholder>
            </span>
          </i-box>
        </div>
        <div class="col-lg-4">
          <div class="container-fluid">
            <div class="row">
              <i-box :noPadding="true">
                <span slot="title">
                  <h5>Top artists</h5>
                </span>
                <span slot="content">
                  <ul class="list-group" v-if="Boolean(chartsData['artists'].top.length)">
                    <li
                      v-for="(artist, index) in chartsData['artists'].top"
                      :key="artist.artist"
                      class="list-group-item small track"
                    >
                      <span class="artist-position pull-left text-bold">{{ index + 1 }}.</span>
                      <span>
                        <growth :value="artist.change" :isPercentage="false" class="pull-right"></growth>
                        <span class="track-artist">{{ artist.artist }}</span>
                        <br />
                      </span>
                    </li>
                  </ul>
                  <content-loader-placeholder
                    v-else
                    slot="unit-content"
                    :type="'ListDividerLoader'"
                  ></content-loader-placeholder>
                </span>
              </i-box>
            </div>
            <div class="row">
              <i-box :noPadding="true">
                <span slot="title">
                  <h5>Top genres</h5>
                </span>
                <span slot="content">
                  <analytics-chart
                    v-if="Boolean(chartsData['genres'].top.length)"
                    type="doughnut"
                    chartStyle="max-height: 200px"
                    :data="chartsData['genres'].top.map(s => [s.genre, s.count])"
                  ></analytics-chart>
                  <content-loader-placeholder
                    v-if="!Boolean(chartsData['genres'].top.length)"
                    :type="'DoughnutChartLoader'"
                    slot="content-loader"
                  ></content-loader-placeholder>
                  <ul class="list-group">
                    <li
                      v-for="(genre, index) in chartsData['genres'].top"
                      :key="genre.genre"
                      class="list-group-item small track"
                    >
                      <span class="artist-position pull-left text-bold">{{ index + 1 }}.</span>
                      <span>
                        <growth :value="genre.change" :isPercentage="false" class="pull-right"></growth>
                        <span class="track-artist">{{ genre.genre }}</span>
                        <br />
                      </span>
                    </li>
                  </ul>
                </span>
              </i-box>
            </div>
            <div class="row">
              <i-box :noPadding="true">
                <span slot="title">
                  <h5>Top decades</h5>
                </span>
                <span slot="content">
                  <ul class="list-group">
                    <analytics-chart
                      v-if="Boolean(chartsData['decades'].top.length)"
                      type="doughnut"
                      chartStyle="max-height: 200px"
                      :data="chartsData['decades'].top.map(s => [s.decade, s.count])"
                    ></analytics-chart>
                    <content-loader-placeholder
                      v-if="!Boolean(chartsData['decades'].top.length)"
                      :type="'DoughnutChartLoader'"
                      slot="content-loader"
                    ></content-loader-placeholder>

                    <ul v-if="Boolean(chartsData['decades'].top.length)">
                      <li
                        v-for="(decade, index) in chartsData['decades'].top"
                        :key="decade.decade"
                        class="list-group-item small track"
                      >
                        <span class="artist-position pull-left text-bold">{{ index + 1 }}.</span>
                        <span>
                          <growth :value="decade.change" :isPercentage="false" class="pull-right"></growth>
                          <span class="track-artist">{{ decade.decade }}'s</span>
                          <br />
                        </span>
                      </li>
                    </ul>
                    <content-loader-placeholder
                      v-else
                      slot="unit-content"
                      :type="'ListDividerLoader'"
                    ></content-loader-placeholder>
                  </ul>
                </span>
              </i-box>
            </div>

            <div class="row">
              <i-box>
                <span slot="title">
                  <h5>Local vs International</h5>
                </span>
                <span slot="content">
                  <analytics-chart
                    v-if="Boolean(processLocalVsInternational.length)"
                    type="doughnut"
                    chartStyle="max-height: 200px"
                    :showLegend="false"
                    :data="processLocalVsInternational"
                  ></analytics-chart>
                  <content-loader-placeholder
                    v-if="!Boolean(processLocalVsInternational.length)"
                    :type="'DoughnutChartLoader'"
                    slot="content-loader"
                  ></content-loader-placeholder>
                </span>
              </i-box>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <i-box :noPadding="true">
            <span slot="title">
              <h5>Top Stations</h5>
            </span>
            <span slot="content">
              <ul class="list-group" v-if="Boolean(chartsData['stations'].top.length)">
                <li
                  v-for="(station, index) in chartsData['stations'].top"
                  :key="station.station"
                  class="list-group-item small track"
                >
                  <span class="track-position pull-left">{{ index + 1 }}</span>
                  <div>
                    <div class="pull-right text-right">
                      <growth :value="station.change" :isPercentage="false"></growth>
                      <div class="text-muted">{{ station.revenue.toLocaleString() }}</div>
                    </div>
                    <span class="track-artist text-muted">{{ station.station }}</span>
                    <br />
                    <span class="track-title">&nbsp;</span>
                  </div>
                </li>
              </ul>
              <content-loader-placeholder v-else slot="unit-content" :type="'ListDividerLoader'"></content-loader-placeholder>
            </span>
          </i-box>
        </div>
      </div>
    </div>
    <div v-else style="padding-top: 12px;">
      <div class="text-center ibox-content">
        <h3>Please contact ColonyLive sales team to activate this feature</h3>
        <el-button size="mini" @click="promptMailToSale" type="primary">Contact Sales</el-button>
      </div>
      <img
        src="../../assets/images/afstereo-blurred.png"
        alt="Afstereo dashboard blured"
        style="width: 100%; height: auto;"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import IBox from '@/components/common/IBox';
import Growth from '@/components/common/Growth';

import HorizontalBarChart from '@/components/charts/HorizontalBarChart';

import AnalyticsUnitBlock from '@/components/analytics/AnalyticsUnitBlock';
import AnalyticsChart from '@/components/analytics/charts/AnalyticsChart';
import AnalyticsTimeSeriesAreaChart from '@/components/analytics/charts/AnalyticsTimeSeriesAreaChart';
import ContentLoaderPlaceholder from '@/components/common/ContentLoaderPlaceholder';

export default {
  name: 'AfstereoDashboard',
  components: {
    IBox,
    Growth,

    HorizontalBarChart,

    AnalyticsUnitBlock,
    AnalyticsChart,
    AnalyticsTimeSeriesAreaChart,
    ContentLoaderPlaceholder,
  },
  data: () => ({
    units: ['minute', 'hour', 'day', 'month', 'year'],
  }),
  props: {
    selectedStation: {
      type: Number,
      default: 0,
    },
    selectedPeriod: {
      type: String,
      default: 'year',
    },
    selectedDate: {
      type: Object,
      default: () => moment(),
    },
    stations: {
      type: Array,
      default: () => [],
    },
    pluginEnabled: {
      type: Boolean,
      default: true,
    },
    chartsData: {
      type: Object,
      default: () => ({
        'hour-stats': { total: 0, growth: 0, points: [] },
        'day-stats': { total: 0, growth: 0, points: [] },
        'month-stats': { total: 0, growth: 0, points: [] },
        'year-stats': { total: 0, growth: 0, points: [] },
        advertisers: { top: [] },
        artists: { top: [] },
        categories: { top: [] },
        countries: { top: [] },
        decades: { top: [] },
        genres: { top: [] },
        tracks: { top: [] },
        stations: { top: [], area: [] },
        local_vs_international: { points: [{ local: 0, international: 0 }] },
        sold_out: {
          hours: {
            '00:00': 0,
            '01:00': 0,
            '02:00': 0,
            '03:00': 0,
            '04:00': 0,
            '05:00': 0,
            '06:00': 0,
            '07:00': 0,
            '08:00': 0,
            '09:00': 0,
            '10:00': 0,
            '11:00': 0,
            '12:00': 0,
            '13:00': 0,
            '14:00': 0,
            '15:00': 0,
            '16:00': 0,
            '17:00': 0,
            '18:00': 0,
            '19:00': 0,
            '20:00': 0,
            '21:00': 0,
            '22:00': 0,
            '23:00': 0,
          },
        },
      }),
    },
  },
  computed: {
    currentStation: {
      get() {
        return this.selectedStation;
      },
      set(station) {
        this.$emit('changeStation', { station });
      },
    },

    // TODO: REFACTOR
    processAdvertisers() {
      const maxCount = Math.max(...this.chartsData.advertisers.top.map(s => s.count));
      const maxRevenue = Math.max(...this.chartsData.advertisers.top.map(s => s.revenue));

      return this.chartsData.advertisers.top.map(s => [
        s.advertiser,
        {
          Spots: Math.round((s.count / maxCount) * 100),
          Revenue: Math.round((s.revenue / maxRevenue) * 100),
        },
      ]);
    },

    // TODO: REFACTOR
    processCategories() {
      const maxCount = Math.max(...this.chartsData.categories.top.map(s => s.count));
      const maxRevenue = Math.max(...this.chartsData.categories.top.map(s => s.revenue));

      return this.chartsData.categories.top.map(s => [
        s.category,
        {
          Spots: Math.round((s.count / maxCount) * 100),
          Revenue: Math.round((s.revenue / maxRevenue) * 100),
        },
      ]);
    },

    processLocalVsInternational() {
      const points = this.chartsData.local_vs_international.points.length
        ? this.chartsData.local_vs_international.points[0]
        : { local: 0, international: 0 };
      return Object.keys(points).map(key => [key, points[key]]);
    },

    processEngagementsVsRevenue() {
      const aliases = {
        revenue: 'Revenue %',
        uniques: 'Engagement %',
      };
      const stats = this.chartsData.stations.area.map(s => {
        const stat = s;
        if (Object.keys(aliases).indexOf(stat[0]) !== -1) {
          stat[0] = aliases[stat[0]];
        }

        return stat;
      });

      return stats;
    },

    processSoldOut() {
      const hours = this.chartsData.sold_out.hours;
      return Object.keys(hours).map(key => [key, hours[key]]);
    },

    isSoldOutAveraging() {
      if (['week', 'month', 'year'].indexOf(this.selectedPeriod) !== -1) {
        return true;
      }
      return false;
    },
  },
  methods: {
    promptMailToSale() {
      window.open('mailto:robby@colonyhq.com?subject=Colony%20Live%20Afstereo%20Plugin%20Request');
    },
  },
};
</script>

<style lang="scss" scoped>
.afstereo-header {
  img {
    height: 45px;
    margin-right: 12px;
    vertical-align: bottom;
  }
}

.afstereo-logo {
  /* There is whitespace on the bottom of the Afstereo logo that messed with the alignment */
  margin-bottom: -8px;
}

.track {
  .track-growth {
    padding-left: 5px;
  }

  .track-position {
    font-size: 22px;
    padding-right: 10px;
  }
}

.bar-chart {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// AfstereoDashboard.vue?519a36c8